import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { api } from '../services/api';
import { Subscription, SubscriptionSummary } from '../../types/subscription';
import { applyDemoCasesToSubscriptionBuilder } from './demoEffects/subscription';

export interface SubscriptionState {
  activeSubscriptionId: number | null;
  activeClusterId: string | null;
  subscriptions: Record<number, Subscription>;
  isSummaryLoading: boolean;
  isSubscriptionLoading: boolean;
}

const initialState: SubscriptionState = {
  activeSubscriptionId: null,
  activeClusterId: null,
  subscriptions: {},
  isSummaryLoading: true,
  isSubscriptionLoading: true,
};

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    setActiveSubscriptionId: (state, action: PayloadAction<number>) => {
      if (state.activeSubscriptionId !== action.payload) {
        state.isSubscriptionLoading = true;
      }

      state.activeSubscriptionId = action.payload;
    },
    setActiveClusterId: (state, action: PayloadAction<string | null>) => {
      state.activeClusterId = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Demo-mode reducer cases live in their own file so this slice has no
    // direct knowledge of demo mode.
    applyDemoCasesToSubscriptionBuilder(builder);

    builder.addMatcher(
      api.endpoints.getSubscriptionDetails.matchFulfilled,
      (state, { payload }) => {
        if (!payload) return;
        const dp = payload.chartData?.dataPoints;
        const subscriptionData = {
          ...payload,
          chartData: dp
            ? {
                dataPoints: {
                  noAlgorithmicProgression: dp.noAlgorithmicProgression ?? [],
                  total: {
                    stable: dp.total?.stable ?? [],
                  },
                },
              }
            : undefined,
        };

        state.subscriptions[payload.id] = subscriptionData;
        state.isSubscriptionLoading = false;
      },
    );

    builder.addMatcher(
      api.endpoints.getSubscriptionSummariesByUserId.matchPending,
      (state) => {
        state.isSummaryLoading = true;
      },
    );

    builder.addMatcher(
      api.endpoints.getAllSubscriptionProfits.matchFulfilled,
      (state, { payload }) => {
        const subscriptionProfits = payload;

        if (
          !subscriptionProfits ||
          Object.keys(subscriptionProfits).length === 0
        ) {
          return;
        }

        Object.entries(subscriptionProfits).forEach(
          ([subscriptionId, profits]) => {
            state.subscriptions[parseInt(subscriptionId)] = {
              ...state.subscriptions[parseInt(subscriptionId)],
              profits,
            };
          },
        );
      },
    );

    builder.addMatcher(
      api.endpoints.getSubscriptionSummariesByUserId.matchFulfilled,
      (state, { payload }) => {
        const subscriptionSummaries = payload.subscriptionSummary;
        state.isSummaryLoading = false;

        if (!subscriptionSummaries?.length) {
          return;
        }

        state.subscriptions = subscriptionSummaries.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.id]: {
              ...state.subscriptions[curr.id],
              ...curr,
              // Summary returns 0 for noAlgorithmicProgressionProfit, so don't overwrite it
              profits: {
                ...curr.profits,
                noAlgorithmicProgressionProfit:
                  curr.profits?.noAlgorithmicProgressionProfit ||
                  state.subscriptions[curr.id]?.profits
                    ?.noAlgorithmicProgressionProfit,
              },
            },
          }),
          {},
        );

        const highestValueSubscription =
          subscriptionSummaries.reduce<SubscriptionSummary | null>(
            (max, curr) =>
              curr.totalBalanceInStable >
              (max?.totalBalanceInStable || -Infinity)
                ? curr
                : max,
            null,
          );

        if (highestValueSubscription?.id && !state.activeSubscriptionId) {
          state.activeSubscriptionId = highestValueSubscription.id;
        }

        // if (!state.activeClusterId) {
        //   const totalValueByCluster = subscriptionSummaries.reduce<
        //     Record<string, number>
        //   >((acc, sub) => {
        //     acc[sub.clusterId] =
        //       (acc[sub.clusterId] || 0) + sub.totalBalanceInStable;
        //     return acc;
        //   }, {});

        //   const clusterId = Object.keys(totalValueByCluster).reduce(
        //     (maxKey, key) =>
        //       totalValueByCluster[key] > totalValueByCluster[maxKey]
        //         ? key
        //         : maxKey,
        //   );

        //   state.activeClusterId = clusterId;
        // }
      },
    );

    builder.addMatcher(
      api.endpoints.updateSubscriptionState.matchFulfilled,
      (state, { payload }) => {
        state.subscriptions[payload.subscriptionId].isActive = payload.isActive;
      },
    );

    builder.addMatcher(
      api.endpoints.updateSubscriptionBalance.matchFulfilled,
      (state, { payload, meta }) => {
        if (!payload?.success) return;

        const { subscriptionId, stableBalance, assetBalance } =
          meta.arg.originalArgs;
        const subscription = state.subscriptions[subscriptionId];
        if (!subscription) return;

        if (typeof stableBalance === 'number') {
          subscription.stableBalance = stableBalance;
        }
        if (typeof assetBalance === 'number') {
          subscription.assetBalance = assetBalance;
        }

        const price = subscription.latestPrice;
        subscription.assetBalanceInStable = subscription.assetBalance * price;
        subscription.stableBalanceInAsset =
          price > 0 ? subscription.stableBalance / price : 0;
        subscription.totalBalanceInStable =
          subscription.stableBalance + subscription.assetBalanceInStable;
        subscription.totalBalanceInAsset =
          subscription.assetBalance + subscription.stableBalanceInAsset;
      },
    );

    builder.addMatcher(
      api.endpoints.removeSubscription.matchFulfilled,
      (state, { payload }) => {
        delete state.subscriptions[payload.subscriptionId];

        if (state.activeSubscriptionId === payload.subscriptionId) {
          const highestValueSubscription = Object.values(
            state.subscriptions,
          ).reduce<SubscriptionSummary | null>(
            (max, curr) =>
              curr.totalBalanceInStable >
              (max?.totalBalanceInStable || -Infinity)
                ? curr
                : max,
            null,
          );

          state.activeSubscriptionId = highestValueSubscription?.id || -1;
        }
      },
    );
  },
});

const selectSubscriptions = (state: RootState) =>
  state.subscription.subscriptions;

export const selectSubscriptionSummaries = createSelector(
  [selectSubscriptions],
  (subscriptions) => {
    return Object.values(subscriptions);
  },
);

export const selectSubscriptionBotFriendlyNames = createSelector(
  [selectSubscriptions],
  (subscriptions) => {
    return Object.values(subscriptions).map((s) => s.botFriendlyName);
  },
);

export const { setActiveSubscriptionId, setActiveClusterId } =
  subscriptionSlice.actions;

export const selectActiveSubscriptionId = (state: RootState) =>
  state.subscription.activeSubscriptionId;

export const selectIsLoadingSubscriptionSummaries = (state: RootState) =>
  state.subscription.isSummaryLoading;

export const selectIsLoadingSubscription = (state: RootState) =>
  state.subscription.isSubscriptionLoading;

export const selectActiveCluster = (state: RootState) =>
  state.subscription.activeClusterId;

export const selectActiveSubscription = (state: RootState) =>
  state.subscription.activeSubscriptionId
    ? state.subscription.subscriptions[state.subscription.activeSubscriptionId]
    : null;

export const selectBaskets = createSelector(
  [selectSubscriptions],
  (subscriptions) => {
    return Object.values(subscriptions)
      .map((s) => s.clusterId)
      .filter((c, i, s) => s.findIndex((si) => si === c) === i);
  },
);

export default subscriptionSlice.reducer;
